export type ServiceRowKey = string | number;
export type ServiceQueryParams = Record<string, unknown>;

type IdentifiableRow = {
  _id?: ServiceRowKey;
  id?: ServiceRowKey;
  key?: ServiceRowKey;
};

export type ApiListResponse<T> = {
  data?: T[] | null;
  totalDocs?: number;
  totalItem?: number;
  limit?: number;
  page?: number;
};

type PaginatedResult<T, K extends "page" | "current"> = {
  data: Array<T & { key?: ServiceRowKey }>;
  totalItem: number;
  pageSize?: number;
} & Partial<Record<K, number>>;

const getDefaultRowKey = <T extends object>(item: T) => {
  const row = item as T & IdentifiableRow;
  return row._id ?? row.id ?? row.key;
};

export const withRowKeys = <T extends object>(
  items: readonly T[] | null | undefined,
  getRowKey: (item: T) => ServiceRowKey | undefined = getDefaultRowKey,
): Array<T & { key?: ServiceRowKey }> =>
  (items ?? []).map((item) => ({
    ...item,
    key: getRowKey(item),
  }));

export const normalizePaginatedResponse = <
  T extends object,
  K extends "page" | "current",
>(
  response: ApiListResponse<T>,
  pageField: K,
  getRowKey?: (item: T) => ServiceRowKey | undefined,
): PaginatedResult<T, K> =>
  ({
    data: withRowKeys(response.data, getRowKey),
    totalItem: response.totalDocs ?? response.totalItem ?? 0,
    pageSize: response.limit,
    [pageField]: response.page,
  }) as PaginatedResult<T, K>;
